import React, { useState, useEffect } from "react";
import apiService from '../../../config';

const CreditorList = () => {
    const [tableRows, setTableRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [editState, setEditState] = useState({});
    const [projectInfo, setProjectInfo] = useState({ title: "", village: "", taluka: "", district: "" });

    const [currentProjectId, setCurrentProjectId] = useState(null);
    const [currentProposalId, setCurrentProposalId] = useState(null);

    // Project Id
    useEffect(() => {
        const getProjectInfo = () => {
            const currentProjectData = localStorage.getItem('currentProjectData');
            if (currentProjectData) {
                try {
                    const projectData = JSON.parse(currentProjectData);
                    return { id: projectData.project_id || projectData.id, name: projectData.project_name || projectData.name };
                } catch (e) { console.error(e); }
            }
            const selectedProject = localStorage.getItem('selectedProject');
            if (selectedProject) {
                try {
                    const project = JSON.parse(selectedProject);
                    return { id: project.project_id || project.id, name: project.project_name || project.name };
                } catch (e) { console.error(e); }
            }
            const userData = localStorage.getItem('userData');
            if (userData) {
                try {
                    const user = JSON.parse(userData);
                    return { id: user.data?.project_id || user.project_id, name: user.data?.project_name || user.project_name || 'Unknown Project' };
                } catch (e) { console.error(e); }
            }
            return { id: null, name: null };
        };
        const info = getProjectInfo();
        setCurrentProjectId(info.id);
    }, []);

    // Proposal Id
    useEffect(() => {
        const getProposalInfo = () => {
            const currentProposal = JSON.parse(localStorage.getItem('userData') || '{}');
            if (currentProposal) {
                try {
                    return {
                        proposalId: currentProposal?.data?.default_proposal ||
                            currentProposal?.data?.proposal_id ||
                            currentProposal?.default_proposal ||
                            currentProposal?.proposal_id || null
                    };
                } catch (e) { console.error(e); }
            }
            return { proposalId: null };
        };
        const info = getProposalInfo();
        setCurrentProposalId(info.proposalId);
    }, []);

    // Fetch data
    useEffect(() => {
        if (!currentProjectId || !currentProposalId) return;

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await apiService.getRailwayLandHolding(currentProjectId, {
                    proposalId: currentProposalId
                });

                const landData = response?.data || [];

                const rows = landData.flatMap((parcel, parcelIndex) => {
                    const owners = parcel.owners || [];
                    return owners.map((owner, ownerIndex) => ({
                        sr: parcelIndex + ownerIndex + 1,
                        landId: parcel.LandAcquisition_id,
                        gatNo: parcel.survey_no || "-",
                        holderName: owner.owner_name || "-",
                        totalArea: parseFloat(owner.owner_total_area || 0).toFixed(2),
                        affectedArea: parseFloat(owner.owner_aqr_area || 0).toFixed(2),
                        remark: parcel.creditor_list_remarks || "",
                        village: parcel.village_name || "",
                        taluka: parcel.taluka_name || "",
                        district: parcel.district_name || ""
                    }));
                });

                // Set location info from first parcel
                if (landData.length > 0) {
                    setProjectInfo({
                        title: response?.proposal_title || "अकोला–खंडवा गेज परिवर्तन–प्रस्तावित ब्रॉडगेजसाठी भूमिसंपादन प्रस्ताव",
                        village: landData[0]?.village_name || "",
                        taluka: landData[0]?.taluka_name || "",
                        district: landData[0]?.district_name || ""
                    });
                }

                setTableRows(rows);
            } catch (err) {
                console.error('Error fetching creditor list:', err);
                setError('माहिती लोड करताना त्रुटी आली.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentProjectId, currentProposalId]);

    const handleEdit = (index, row) => {
        setEditState(prev => ({ ...prev, [index]: { remark: row.remark } }));
    };

    const handleChange = (index, value) => {
        setEditState(prev => ({ ...prev, [index]: { remark: value } }));
    };

    const handleSave = async (index, landId) => {
        const edited = editState[index];
        if (!edited) return;
        try {
            await apiService.updateRailwayLandHolding(landId, {
                creditor_list_remarks: edited.remark
            });
            setTableRows(prev => prev.map((r, i) =>
                i === index ? { ...r, remark: edited.remark } : r
            ));
            setEditState(prev => {
                const next = { ...prev };
                delete next[index];
                return next;
            });
        } catch (err) {
            console.error('Error updating remark:', err);
            alert('अपडेट करताना त्रुटी आली.');
        }
    };

    const handleCancel = (index) => {
        setEditState(prev => {
            const next = { ...prev };
            delete next[index];
            return next;
        });
    };

    return (
        <div className="bg-white text-black p-6">
            {/* Heading */}
            <div className="text-center mb-4">
                <h2 className="font-semibold">अल्प – मुदतदार यादी</h2>
                <p className="text-sm mt-1">{projectInfo.title}</p>
            </div>

            {/* Location Info */}
            <div className="flex justify-between text-sm mb-4">
                <p>मौजे : {projectInfo.village}</p>
                <p>तालुका : {projectInfo.taluka}</p>
                <p>जिल्हा : {projectInfo.district}</p>
            </div>

            {loading && <p className="text-center text-blue-600 mb-4">लोड होत आहे...</p>}
            {error && <p className="text-center text-red-600 mb-4">{error}</p>}

            {/* Table */}
            <table className="w-full border border-black border-collapse text-sm">
                <thead>
                    <tr>
                        <th className="border border-black p-2">अ. क्र.</th>
                        <th className="border border-black p-2">गट नंबर <br />/ सर्व्हे नंबर</th>
                        <th className="border border-black p-2">संपादित मालकाचे नाव</th>
                        <th className="border border-black p-2">'क' मध्ये नमूद क्षेत्र <br />(हेक्टर-आर-गुंठे)</th>
                        <th className="border border-black p-2">बाधित होणारे क्षेत्र <br />(हेक्टर-आर-गुंठे)</th>
                        <th className="border border-black p-2">शेरा</th>
                        <th className="border border-black p-2">क्रिया</th>
                    </tr>
                </thead>
                <tbody>
                    {tableRows.map((row, index) => {
                        const isEditing = !!editState[index];
                        return (
                            <tr key={index}>
                                <td className="border border-black p-2 text-center">{row.sr}</td>
                                <td className="border border-black p-2 text-center">{row.gatNo}</td>
                                <td className="border border-black p-2">{row.holderName}</td>
                                <td className="border border-black p-2 text-center">{row.totalArea}</td>
                                <td className="border border-black p-2 text-center">{row.affectedArea}</td>
                                <td className="border border-black p-2">
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            value={editState[index].remark}
                                            onChange={(e) => handleChange(index, e.target.value)}
                                            className="border border-gray-400 rounded px-1 py-0.5 w-full text-sm"
                                        />
                                    ) : (
                                        row.remark || "-"
                                    )}
                                </td>
                                <td className="border border-black p-2 text-center">
                                    {isEditing ? (
                                        <div className="flex gap-1 justify-center">
                                            <button
                                                onClick={() => handleSave(index, row.landId)}
                                                className="bg-green-600 text-white px-2 py-0.5 rounded text-xs hover:bg-green-700"
                                            >
                                                जतन
                                            </button>
                                            <button
                                                onClick={() => handleCancel(index)}
                                                className="bg-gray-400 text-white px-2 py-0.5 rounded text-xs hover:bg-gray-500"
                                            >
                                                रद्द
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => handleEdit(index, row)}
                                            className="bg-blue-600 text-white px-2 py-0.5 rounded text-xs hover:bg-blue-700"
                                        >
                                            संपादित
                                        </button>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* Footer */}
            <div className="mt-8 text-center text-sm">
                <p>
                    उपकार्यकारी अभियंता, (बांधकाम विभाग), <br />
                    दक्षिण मध्य रेल्वे, अकोला
                </p>
            </div>
        </div>
    );
};

export default CreditorList;